<template>
    <header
        ref="siteHeader"
        class="header" 
        :class="{
            'header--scrolled': !isAtTop
        }"
    >
        <div class="container">
            <div class="header-grid">
                <div class="header-grid__logo">
                    <a href="/" class="header__logo logo" aria-label="Go to home page">
                        <slot name="site-logo"></slot>
                    </a>
                </div>
                <div class="header-grid__contact">
                    <slot name="contact-links"></slot>
                </div>
                <div class="header-grid__commerce-controls">
                    <slot name="commerce-controls"></slot>
                    <div class="link-list justify-content-end">
                        <slot name="controls"></slot> 
                        <BasketButton></BasketButton>
                    </div>
                </div>
                <div class="header-grid__rm-logo">
                    <slot name="site-logo-2"></slot>
                </div>
                <div class="header-grid__navigation">
                    <template v-if="showMegaNav && navigationData.length > 0">
                        <MegaNavigation :navigationData="navigationData" />
                    </template>
                    <DrawerNavigation
                        v-if="showMobileNav && navigationData.length > 0"
                        :navigationData="navigationData"
                    >
                        <template #mobilecontrols>
                            <slot name="mobilenavcontrols"></slot> 
                        </template>
                    </DrawerNavigation>
                </div>
            </div>
        </div>
    </header>

    <!-- Compact sticky header that appears only when main header is off screen -->
    <transition name="header-sticky-fade">
        <div
            v-if="showStickyNav && showMegaNav && navigationData.length > 0"
            class="header-sticky-nav"
        >
            <div class="header-sticky-nav__bg"></div>

            <div class="container">
                <div class="header-sticky-nav__inner">
                    <div class="header-sticky-nav__logo-wrapper">
                        <a href="/" class="header-sticky-nav__logo" aria-label="Go to home page">

                            <svg class="smaller-logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 190.64 190.99">
                                <path fill="#eb650a" d="M4.45 87.75v38.2c0 33.46 19.37 60.58 60.58 60.58h60.57c40.52 0 60.58-27.12 60.58-60.58v-38.2"/>
                                <path fill="none" stroke="#fff" stroke-width="4" d="M65.04 5.34c-33.46 0-60.59 48.95-60.59 82.41v38.2c0 33.46 17.67 60.58 60.58 60.58h60.57c41.65 0 60.58-27.12 60.58-60.58v-38.2c0-33.46-27.12-82.41-60.58-82.41H65.03Z"/>
                                <path fill="#252729" d="M2.67 2.82v94.9c0 33.46 22.84 60.58 64.65 60.58h56c39.42 0 64.65-27.12 64.65-60.58V2.67z"/>
                                <path fill="none" stroke="#fff" stroke-width="4" d="M4.31 2.82v95.62c0 33.46 21.2 60.58 63.01 60.58h56c41.11 0 63.01-27.12 63.01-60.58V2.67z"/>
                                <path fill="none" d="M0 0h190.64v190.99H0z"/>
                                <path fill="#fff" d="M64.17 30.25H29.15v90.34h17.64V86.58h17.38c18.65 0 29.82-12.81 29.82-28.17S82.82 30.24 64.17 30.24Zm-.89 40.48h-16.5V45.99h16.5c7.99 0 13.07 5.08 13.07 12.43s-5.07 12.31-13.07 12.31"/>
                                <path fill="#eb650a" d="M161.5 120.7v-12.33h-44.08V81.19h37.6v-12.2h-37.6V42.57h44.08V30.25h-57.8v90.45z"/>
                            </svg>

                            <slot name="site-logo"></slot>
                        </a>
                    </div>

                    <MegaNavigation :navigationData="navigationData" />

                    <div class="header-sticky-nav__controls">
                        <slot name="controls"></slot>
                        <BasketButton></BasketButton>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import DrawerNavigation from '../components/DrawerNavigation.vue';
import MegaNavigation from '../components/MegaNavigation.vue';
import BasketButton from './BasketButton.vue';

function debounce(func, delay) {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
    };
}

export default {
    name: 'SiteHeader',
    components: {
        DrawerNavigation,
        MegaNavigation,
        BasketButton
    },
    props: {
        navigationData: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    data() {
        return {
            showMobileNav: false,
            showMegaNav: false,
            isAtTop: true,
            showStickyNav: false,
            isHeaderInView: true
        };
    },
    methods: {
        checkScreenWidth() {
            this.showMobileNav = window.innerWidth < 1200;
            this.showMegaNav = window.innerWidth >= 1200;
        },
        handleScroll() {
            const currentY = window.scrollY || 0;
            this.isAtTop = currentY === 0;
        },
        handleResize() {
            this.checkScreenWidth();
        }
    },
    mounted() {
        this.debouncedResizeHandler = debounce(this.handleResize, 100);

        this.handleResize();
        this.handleScroll();

        // Listeners
        window.addEventListener('resize', this.debouncedResizeHandler);
        window.addEventListener('orientationchange', this.handleResize);
        window.addEventListener('scroll', this.handleScroll);

        const headerEl = this.$refs.siteHeader;

        if (headerEl && 'IntersectionObserver' in window) {
            this.headerObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        this.isHeaderInView = entry.isIntersecting;

                        if (entry.isIntersecting) {
                            // Main header visible, hide sticky so it never overlaps
                            this.showStickyNav = false;
                        } else {
                            // Main header fully off screen, show sticky
                            this.showStickyNav = true;
                        }
                    });
                },
                {
                    root: null,
                    threshold: 0
                }
            );

            this.headerObserver.observe(headerEl);
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.debouncedResizeHandler);
        window.removeEventListener('orientationchange', this.handleResize);
        window.removeEventListener('scroll', this.handleScroll);

        if (this.headerObserver) {
            this.headerObserver.disconnect();
        }
    }
};
</script>